/**
 * TeamSection — People + Organizations for projects
 * Replaces the old researcher inline editor in QuickEntryTab
 * S10 Wave 2
 */

import { useCallback, useEffect, useState } from 'react'
import { authFetchJson } from '../../lib/authFetch'
import { CompletableSection } from '../ui/CompletableSection'
import type { SectionCompletionState } from '../../hooks/useSectionCompletions'
import { PeopleAutocomplete } from './PeopleAutocomplete'
import { OrgAutocomplete, type Organization } from './OrgAutocomplete'
import { PROJECT_PERSON_ROLE_LABELS, PROJECT_ORG_ROLE_LABELS, PROJECT_PERSON_ROLES, PROJECT_ORG_ROLES, type ProjectPersonRole, type ProjectOrgRole } from '@hos/shared/constants'

interface ProjectPerson {
  id: number
  person_id: number
  name: string
  email?: string
  title?: string
  role: ProjectPersonRole
  created_at: string
}

interface ProjectOrganization {
  id: number
  organization_id: number
  name: string
  type: string
  role: ProjectOrgRole
  created_at: string
}

interface TeamSectionProps {
  projectId: number
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

export function TeamSection({
  projectId,
  sectionCompletions: sc,
  tabId = 'qe',
}: TeamSectionProps) {
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId, sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId, sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId, sectionId)

  // ── People ──
  const [people, setPeople] = useState<ProjectPerson[]>([])
  const [peopleLoading, setPeopleLoading] = useState(false)
  const [peopleError, setPeopleError] = useState<string | null>(null)
  const [selectedPerson, setSelectedPerson] = useState<{ id: number; name: string } | null>(null)
  const [selectedPersonRole, setSelectedPersonRole] = useState<ProjectPersonRole>('researcher')

  // ── Organizations ──
  const [organizations, setOrganizations] = useState<ProjectOrganization[]>([])
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [orgsError, setOrgsError] = useState<string | null>(null)
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [selectedOrgRole, setSelectedOrgRole] = useState<ProjectOrgRole>('partner')

  // Load people on mount
  useEffect(() => {
    loadPeople()
  }, [projectId])

  // Load organizations on mount
  useEffect(() => {
    loadOrganizations()
  }, [projectId])

  const loadPeople = useCallback(async () => {
    setPeopleLoading(true)
    setPeopleError(null)
    try {
      const data = await authFetchJson<{ people: ProjectPerson[] }>(
        `/api/v2/projects/${projectId}/people`
      )
      setPeople(data.people || [])
    } catch (err: any) {
      setPeopleError(err.message || 'Failed to load people')
    } finally {
      setPeopleLoading(false)
    }
  }, [projectId])

  const loadOrganizations = useCallback(async () => {
    setOrgsLoading(true)
    setOrgsError(null)
    try {
      const data = await authFetchJson<{ organizations: ProjectOrganization[] }>(
        `/api/v2/projects/${projectId}/organizations`
      )
      setOrganizations(data.organizations || [])
    } catch (err: any) {
      setOrgsError(err.message || 'Failed to load organizations')
    } finally {
      setOrgsLoading(false)
    }
  }, [projectId])

  // Add person
  const handleAddPerson = useCallback(async () => {
    if (!selectedPerson) return

    setPeopleLoading(true)
    setPeopleError(null)

    try {
      const link = await authFetchJson<ProjectPerson>(
        `/api/v2/projects/${projectId}/people`,
        {
          method: 'POST',
          json: {
            person_id: selectedPerson.id,
            role: selectedPersonRole,
          },
        }
      )
      setPeople((prev) => [...prev, link])
      setSelectedPerson(null)
      setSelectedPersonRole('researcher')
    } catch (err: any) {
      setPeopleError(err.message || 'Failed to add person')
    } finally {
      setPeopleLoading(false)
    }
  }, [projectId, selectedPerson, selectedPersonRole])

  // Edit person role
  const handleEditPersonRole = useCallback(
    async (linkId: number, newRole: ProjectPersonRole) => {
      // Optimistic update
      setPeople((prev) =>
        prev.map((p) => (p.id === linkId ? { ...p, role: newRole } : p))
      )
      setPeopleError(null)

      try {
        await authFetchJson(`/api/v2/projects/${projectId}/people/${linkId}`, {
          method: 'PATCH',
          json: { role: newRole },
        })
      } catch (err: any) {
        // Revert on error
        await loadPeople()
        setPeopleError(err.message || 'Failed to update role')
      }
    },
    [projectId, loadPeople]
  )

  // Delete person
  const handleDeletePerson = useCallback(
    async (linkId: number) => {
      // Optimistic remove
      setPeople((prev) => prev.filter((p) => p.id !== linkId))
      setPeopleError(null)

      try {
        await authFetchJson(`/api/v2/projects/${projectId}/people/${linkId}`, {
          method: 'DELETE',
        })
      } catch (err: any) {
        // Revert on error
        await loadPeople()
        setPeopleError(err.message || 'Failed to delete person')
      }
    },
    [projectId, loadPeople]
  )

  // Add organization
  const handleAddOrg = useCallback(async () => {
    if (!selectedOrg) return

    setOrgsLoading(true)
    setOrgsError(null)

    try {
      const link = await authFetchJson<ProjectOrganization>(
        `/api/v2/projects/${projectId}/organizations`,
        {
          method: 'POST',
          json: {
            organization_id: selectedOrg.id,
            role: selectedOrgRole,
          },
        }
      )
      setOrganizations((prev) => [...prev, link])
      setSelectedOrg(null)
      setSelectedOrgRole('partner')
    } catch (err: any) {
      setOrgsError(err.message || 'Failed to add organization')
    } finally {
      setOrgsLoading(false)
    }
  }, [projectId, selectedOrg, selectedOrgRole])

  // Edit org role
  const handleEditOrgRole = useCallback(
    async (linkId: number, newRole: ProjectOrgRole) => {
      // Optimistic update
      setOrganizations((prev) =>
        prev.map((o) => (o.id === linkId ? { ...o, role: newRole } : o))
      )
      setOrgsError(null)

      try {
        await authFetchJson(`/api/v2/projects/${projectId}/organizations/${linkId}`, {
          method: 'PATCH',
          json: { role: newRole },
        })
      } catch (err: any) {
        // Revert on error
        await loadOrganizations()
        setOrgsError(err.message || 'Failed to update role')
      }
    },
    [projectId, loadOrganizations]
  )

  // Delete org
  const handleDeleteOrg = useCallback(
    async (linkId: number) => {
      // Optimistic remove
      setOrganizations((prev) => prev.filter((o) => o.id !== linkId))
      setOrgsError(null)

      try {
        await authFetchJson(`/api/v2/projects/${projectId}/organizations/${linkId}`, {
          method: 'DELETE',
        })
      } catch (err: any) {
        // Revert on error
        await loadOrganizations()
        setOrgsError(err.message || 'Failed to delete organization')
      }
    },
    [projectId, loadOrganizations]
  )

  return (
    <CompletableSection
      title="Project Team"
      sectionId="qe-team"
      isComplete={isComplete('qe-team')}
      completedByName={getInfo('qe-team')?.completed_by_name}
      completedAt={getInfo('qe-team')?.completed_at}
      onToggle={() => toggle('qe-team')}
    >
      <div className="space-y-6">
        {/* Error */}
        {(peopleError || orgsError) && (
          <div className="px-3 py-2 bg-red-900/30 border border-red-700/50 rounded-lg text-[12px] text-red-300">
            {peopleError || orgsError}
          </div>
        )}

        {/* People Section */}
        <div>
          <h4 className="text-[13px] font-semibold text-[#e8e8f0] mb-3">People</h4>

          {/* People list */}
          {people.length > 0 && (
            <div className="mb-4 space-y-2">
              {people.map((person) => (
                <div
                  key={person.id}
                  className="flex items-center justify-between bg-[#12122a] border border-[#2a2a44] rounded-lg p-3 hover:border-[#3a3a54]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-[#e8e8f0]">
                      {person.name}
                    </div>
                    {(person.email || person.title) && (
                      <div className="text-[12px] text-[#8b8ba0]">
                        {person.email && <div>{person.email}</div>}
                        {person.title && <div>{person.title}</div>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <select
                      value={person.role}
                      onChange={(e) =>
                        handleEditPersonRole(person.id, e.target.value as ProjectPersonRole)
                      }
                      className="
                        px-2 py-1 text-[12px]
                        bg-[#0a0a1e] border border-[#2a2a44]
                        rounded text-[#e8e8f0]
                        focus:border-[#3a3a54]
                      "
                    >
                      {PROJECT_PERSON_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {PROJECT_PERSON_ROLE_LABELS[role]}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleDeletePerson(person.id)}
                      className="text-[12px] text-[#E61B25] hover:text-[#d41620] font-medium whitespace-nowrap"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add person form */}
          <div className="space-y-2">
            <PeopleAutocomplete
              value={selectedPerson}
              onChange={setSelectedPerson}
              selectedRole={selectedPersonRole}
              placeholder="Search or create person..."
            />
            <div className="flex gap-2">
              <select
                value={selectedPersonRole}
                onChange={(e) =>
                  setSelectedPersonRole(e.target.value as ProjectPersonRole)
                }
                className="
                  flex-1 px-3 py-2 text-[13px]
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg text-[#e8e8f0]
                  focus:border-[#3a3a54]
                "
              >
                {PROJECT_PERSON_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {PROJECT_PERSON_ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddPerson}
                disabled={!selectedPerson || peopleLoading}
                className="
                  px-4 py-2
                  bg-[#E61B25] text-white
                  rounded-lg
                  text-[13px] font-medium
                  hover:bg-[#d41620]
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors
                  whitespace-nowrap
                "
              >
                {peopleLoading ? 'Adding...' : 'Add Person'}
              </button>
            </div>
          </div>
        </div>

        {/* Organizations Section */}
        <div className="border-t border-[#2a2a44] pt-4">
          <h4 className="text-[13px] font-semibold text-[#e8e8f0] mb-3">
            Institutions & Partners
          </h4>

          {/* Organizations list */}
          {organizations.length > 0 && (
            <div className="mb-4 space-y-2">
              {organizations.map((org) => (
                <div
                  key={org.id}
                  className="flex items-center justify-between bg-[#12122a] border border-[#2a2a44] rounded-lg p-3 hover:border-[#3a3a54]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-[#e8e8f0]">
                      {org.name}
                    </div>
                    <div className="text-[12px] text-[#8b8ba0]">
                      {org.type}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <select
                      value={org.role}
                      onChange={(e) =>
                        handleEditOrgRole(org.id, e.target.value as ProjectOrgRole)
                      }
                      className="
                        px-2 py-1 text-[12px]
                        bg-[#0a0a1e] border border-[#2a2a44]
                        rounded text-[#e8e8f0]
                        focus:border-[#3a3a54]
                      "
                    >
                      {PROJECT_ORG_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {PROJECT_ORG_ROLE_LABELS[role]}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleDeleteOrg(org.id)}
                      className="text-[12px] text-[#E61B25] hover:text-[#d41620] font-medium whitespace-nowrap"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add org form */}
          <div className="space-y-2">
            <OrgAutocomplete
              value={selectedOrg}
              onChange={setSelectedOrg}
              placeholder="Search or create organization..."
            />
            <div className="flex gap-2">
              <select
                value={selectedOrgRole}
                onChange={(e) =>
                  setSelectedOrgRole(e.target.value as ProjectOrgRole)
                }
                className="
                  flex-1 px-3 py-2 text-[13px]
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg text-[#e8e8f0]
                  focus:border-[#3a3a54]
                "
              >
                {PROJECT_ORG_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {PROJECT_ORG_ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddOrg}
                disabled={!selectedOrg || orgsLoading}
                className="
                  px-4 py-2
                  bg-[#E61B25] text-white
                  rounded-lg
                  text-[13px] font-medium
                  hover:bg-[#d41620]
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors
                  whitespace-nowrap
                "
              >
                {orgsLoading ? 'Adding...' : 'Add Organization'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </CompletableSection>
  )
}
